import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import { UnstyledButton } from '../../components/UnstyledButton'
import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import type { ReactButtonProps } from '../../types'
import { CssVars, NeutralColorToken } from '../themes'
import { Spinner16 } from './Spinner'
import { useTheme, useThemeColorBase } from './ThemeProvider'
import { Type } from './Type'

const KNOB_SIZE = '26px'
const WIDTH = '72px'
const GUTTER_SIZE = '2px'
const BORDER_WIDTH = '1px'
const TRANSITION_DURATION = '200ms'
const TRANSITION_TIMING_FUNCTION = 'ease-out'
const KNOB_SLIDE_DISTANCE = `calc(${WIDTH} - ${KNOB_SIZE} - 2 * ${GUTTER_SIZE} - 2 * ${BORDER_WIDTH})`

type OnChange = ({
  event,
  isChecked,
}: {
  isChecked: boolean
  event: React.MouseEvent<HTMLButtonElement, MouseEvent>
}) => void

type OnClick = NonNullable<ReactButtonProps['onClick']>

type BaseProps = {
  /**
   * The state of the checkbox. "disabled" marks it as disabled. "loading" marks
   * it as loading and renders a spinner to the toggle's left.
   */
  state?: 'default' | 'disabled' | 'loading' | false
  /**
   * `true` if the Toggle is turned on, `false` if it's turned off.
   */
  isChecked: boolean
  /**
   * An identifier used for tests.
   */
  'data-testid'?: string

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation

  /**
   * Position of the spinner. By default it is displayed on the left side (start).
   */
  spinnerPosition?: 'start' | 'end'

  /**
   * Show the on/off labels. By default they are shown.
   */
  showOnOffLabels?: boolean
} & (
  | {
      /**
       * **DEPRECATED** please use `onClick` instead as with any normal `<button>` element
       *
       * Called when the user changes the value of the Toggle to either checked or unchecked.
       *
       * @deprecated please use `onClick` instead as with any normal `<button>` element
       */
      onChange: OnChange
    }
  | {
      /**
       * Called when the Toggle button is clicked on.
       */
      onClick: OnClick
    }
)
type ToggleBaseButtonProps = Omit<
  ReactButtonProps,
  | keyof BaseProps
  | 'aria-busy'
  | 'aria-checked'
  | 'className'
  | 'css'
  | 'onChange'
  | 'onClick'
  | 'ref'
  | 'role'
  | 'style'
>

type Props = BaseProps & ToggleBaseButtonProps

/**
 * Renders a control that allows users to disable or enable something consequential,
 * like a global setting.
 */
export function Toggle({
  'data-testid': testId,
  isChecked,
  overrideRootStyles,
  spinnerPosition = 'start',
  showOnOffLabels = true,
  state = 'default',
  ...props
}: Props) {
  const isDisabled = state === 'disabled' || state === 'loading'

  let buttonProps: ToggleBaseButtonProps = {}
  let onClick: OnClick | undefined
  let onChange: OnChange | undefined
  if ('onClick' in props) {
    const { onClick: extractedOnClick, ...filteredButtonProps } = props
    buttonProps = filteredButtonProps
    onClick = extractedOnClick
  }
  if ('onChange' in props) {
    const { onChange: extractedOnChange, ...filteredButtonProps } = props
    buttonProps = filteredButtonProps
    onChange = extractedOnChange
  }

  const handleOnClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onClick) {
        onClick(event)
      }
      if (onChange) {
        onChange({ event, isChecked: !isChecked })
      }
    },
    [onChange, onClick, isChecked]
  )

  return (
    <UnstyledButton
      aria-busy={state === 'loading'}
      aria-checked={isChecked}
      css={[
        {
          alignItems: 'center',
          // NOTE(yan): currently the web-app applies the .no-focus-outline
          //  class to the body if the user is using a pointing device to
          //  focus elements, so we respect that classname here.
          // TODO(yan): once :has() selector is fully supported, move this to
          //  the div below and the div can check for its parent's focus state
          'body:not(.no-focus-outline) &:focus-visible > div': {
            borderColor: isChecked ? CssVars.neutral.bg3 : undefined,
            boxShadow: `
              0 0 0 1px ${CssVars.focus.innerColor},
              0 0 0 4px ${CssVars.focus.outerColor};
            `,
          },
          cursor: isDisabled ? 'auto' : 'pointer',
          display: 'inline-flex',
          outline: 0,
          position: 'relative',
        },
        // NOTE(jare): Always render space for the spinner, whether it's visible or not
        spinnerPosition === 'end'
          ? {
              paddingRight: `calc(24px + ${CssVars.size.gutterWidthSm})`,
            }
          : {
              paddingLeft: `calc(24px + ${CssVars.size.gutterWidthSm})`,
            },
        overrideRootStyles,
      ]}
      data-testid={testId}
      disabled={isDisabled}
      onClick={handleOnClick}
      role="switch"
      {...buttonProps}
    >
      {state === 'loading' && (
        <div
          css={[
            {
              position: 'absolute',
            },
            spinnerPosition === 'end'
              ? {
                  right: 0,
                }
              : {
                  left: 0,
                },
          ]}
        >
          <Spinner16 color={NeutralColorToken.Fg2} />
        </div>
      )}
      <div
        aria-hidden="true"
        css={[
          {
            alignItems: 'center',
            backgroundColor: CssVars.neutral.bg1,
            borderColor: CssVars.neutral.bg3,
            borderRadius: 36,
            borderStyle: 'solid',
            borderWidth: BORDER_WIDTH,
            boxSizing: 'border-box',
            flexShrink: 0,
            height: 32,
            // NOTE(jare): Hide overflow so that the text labels inside the
            // switch get cut off when they're translated past the edges of
            // this element
            overflow: 'hidden',
            padding: GUTTER_SIZE,
            position: 'relative',
            transition: `background-color ${TRANSITION_DURATION}, border-color ${TRANSITION_DURATION}, opacity ${TRANSITION_DURATION}`,
            width: WIDTH,
          },
          isChecked
            ? [
                {
                  backgroundColor: CssVars.semantic.primary.base,
                  borderColor: 'transparent',
                },
                isDisabled
                  ? {
                      opacity: 0.4,
                    }
                  : {
                      'input:active + &, &:active': {
                        backgroundColor: CssVars.semantic.primary.pressed,
                      },
                      'input:hover + &, &:hover': {
                        backgroundColor: CssVars.semantic.primary.hover,
                      },
                    },
              ]
            : [
                isDisabled
                  ? {
                      backgroundColor: CssVars.neutral.bg2,
                    }
                  : {
                      'input:active + &, &:active': {
                        backgroundColor: CssVars.neutral.bg3,
                      },
                      'input:hover + &, &:hover': {
                        backgroundColor: CssVars.neutral.bg2,
                      },
                    },
              ],
        ]}
      >
        <div
          css={[
            {
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              transitionDuration: TRANSITION_DURATION,
              transitionProperty: 'transform',
              transitionTimingFunction: TRANSITION_TIMING_FUNCTION,
              userSelect: 'none',
              width: `calc(100% + ${KNOB_SLIDE_DISTANCE})`,
            },
            !isChecked && {
              transform: `translateX(calc(-1 * ${KNOB_SLIDE_DISTANCE}))`,
            },
          ]}
        >
          <OnOffStateLabel
            css={{ left: 13, position: 'absolute' }}
            isDisabled={isDisabled}
            isVisible={isChecked && showOnOffLabels}
            side="on"
          />
          <Knob isChecked={isChecked} isDisabled={isDisabled} />
          <OnOffStateLabel
            css={{ position: 'absolute', right: 13 }}
            isDisabled={isDisabled}
            isVisible={!isChecked && showOnOffLabels}
            side="off"
          />
        </div>
      </div>
    </UnstyledButton>
  )
}

function Knob({
  className,
  isChecked,
  isDisabled,
}: {
  className?: string
  isChecked: boolean
  isDisabled: boolean
}) {
  const theme = useTheme()
  const novaBase = useThemeColorBase(ExtendedPaletteColorName.Nova)
  return (
    <div
      className={className}
      css={[
        {
          backgroundColor: novaBase,
          borderRadius: '50%',
          boxShadow: `0 1px 4px rgba(0, 0, 0, 0.2)`,
          height: KNOB_SIZE,
          width: KNOB_SIZE,
        },
        !isChecked &&
          isDisabled &&
          theme.isDark && {
            opacity: 0.4,
          },
      ]}
    />
  )
}

function OnOffStateLabel({
  className,
  isDisabled,
  isVisible,
  side,
}: {
  className?: string
  isVisible: boolean
  isDisabled: boolean
  side: 'on' | 'off'
}) {
  return (
    <span
      aria-hidden={!isVisible}
      className={className}
      css={[
        {
          alignItems: 'center',
          display: 'flex',
          transitionDuration: TRANSITION_DURATION,
          transitionProperty: 'opacity',
          transitionTimingFunction: TRANSITION_TIMING_FUNCTION,
        },
        !isVisible && {
          opacity: 0,
        },
      ]}
    >
      {/*
       * TODO(jare): internationalize these strings (maybe via context?).
       * When we do, we may need to update the Toggle to support dynamic
       * label widths.
       */}
      {side === 'on' ? (
        <Type.Text color={NeutralColorToken.Bg1} isBold size="S">
          On
        </Type.Text>
      ) : (
        <Type.Text color={isDisabled ? NeutralColorToken.Fg3 : null} size="S">
          Off
        </Type.Text>
      )}
    </span>
  )
}
